import React from 'react';
import { motion } from 'framer-motion';

interface MemberDetails {
  photo: string;
  fullName: string;
  permanentEnrollmentNumber: string;
  memberType: string;
  rank: string;
  designation: string;
  branch: string;
  memberStatus: string;
  languagesKnown: string;
  dateOfBirth: string;
  currentCity: string;
  currentDistrict: string;
  currentState: string;
  currentPinCode: string;
  bloodGroup: string;
  validUntil?: string;
}

interface SearchCardProps {
  member: MemberDetails;
  verificationLayout?: boolean;
}

const SearchCard: React.FC<SearchCardProps> = ({ member, verificationLayout = false }) => {
  const detailClassName = verificationLayout
    ? 'flex min-w-0 items-center justify-center rounded-lg border border-slate-600/40 border-l-2 border-l-slate-400 bg-slate-800/70 px-3 py-2.5 shadow-sm shadow-black/20 transition-colors hover:border-slate-500 hover:border-l-blue-400 hover:bg-slate-800'
    : '';
  const detailValueClassName = verificationLayout ? 'min-w-0 text-center' : '';

  const formatWord = (word: string) => {
    const w = word.trim();
    if (!w) return w;

    if (w.toLowerCase() === 'it') return 'IT';
    if (w.toUpperCase() === w && /[A-Z]/.test(w)) return w;
    if (/[0-9]/.test(w)) return w;

    return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
  };

  const formatTitle = (value: string) => {
    if (!value) return '';

    return value
      .split(/(\s+|,)/)
      .map((part) => {
        if (part === ',' || /^\s+$/.test(part)) return part;
        return formatWord(part);
      })
      .join('');
  };

  const formatStatus = (value: string) => {
    const v = (value || '').trim();
    if (!v) return '';
    return v.charAt(0).toUpperCase() + v.slice(1).toLowerCase();
  };

  const formatDate = (value?: string) => {
    if (!value) return '—';
    const dateOnly = value.slice(0, 10);
    const parts = dateOnly.split('-');
    return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : value;
  };

  const getBadges = () => {
    if (verificationLayout) {
      return [
        <span key="memberTypeDesignation" className="justify-self-start px-3 py-1 bg-blue-500/10 text-blue-400 rounded-full text-sm font-medium">
          {[formatTitle(member.memberType), formatTitle(member.rank || member.designation)].filter(Boolean).join(' ') || '—'}
        </span>,
        <span key="branch" className="justify-self-start px-3 py-1 bg-amber-500/10 text-amber-400 rounded-full text-sm font-medium">
          {formatTitle(member.branch) || '—'}
        </span>
      ];
    }

    const badges = [
      <span key="memberType" className="px-3 py-1 bg-blue-500/10 text-blue-400 rounded-full text-sm font-medium">
        {formatTitle(member.memberType)}
      </span>
    ];

    const titleOrRank = member.rank || member.designation;
    if (titleOrRank) {
      badges.push(
        <span key="designation" className="px-3 py-1 bg-purple-500/10 text-purple-400 rounded-full text-sm font-medium">
          {formatTitle(titleOrRank)}
        </span>
      );
    }

    if (member.branch) {
      badges.push(
        <span key="branch" className="px-3 py-1 bg-amber-500/10 text-amber-400 rounded-full text-sm font-medium">
          {formatTitle(member.branch)}
        </span>
      );
    }
    
    // Only show status badge if not pending
    if (member.memberStatus && member.memberStatus.toLowerCase() !== 'pending') {
      badges.push(
        <span key="status" className="px-3 py-1 bg-green-500/10 text-green-400 rounded-full text-sm font-medium">
          {formatStatus(member.memberStatus)}
        </span>
      );
    }
    
    return badges;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5 }}
      className="w-full bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl shadow-xl overflow-hidden"
    >
      <div className={verificationLayout ? 'p-4 sm:p-6' : 'p-6 sm:p-8'}>
        <div className={`gap-6 h-full ${verificationLayout ? 'grid grid-cols-1 items-start md:grid-cols-[180px_minmax(0,1fr)]' : 'flex flex-col sm:flex-row'}`}>
          {/* Profile Image Section */}
          <motion.div
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2 }}
            className={verificationLayout ? 'mr-auto flex-shrink-0 self-start' : 'flex-shrink-0 mx-auto sm:mx-0 min-w-[250px]'}
          >
            <div className={`relative rounded-xl overflow-hidden ring-2 ring-blue-500/30 ${verificationLayout ? 'h-[220px] w-[180px]' : 'w-[250px] h-[300px]'}`}>
              <img
                src={member.photo || '/default-avatar.png'}
                alt={member.fullName}
                className="absolute inset-0 w-full h-full object-cover object-center"
              />
              {verificationLayout && (
                <span className={`absolute right-2 top-2 rounded-full border px-2.5 py-1 text-xs font-semibold shadow-lg backdrop-blur-sm ${member.memberStatus.toLowerCase() === 'active' ? 'border-green-400/40 bg-green-500/90 text-white' : 'border-red-400/40 bg-red-500/90 text-white'}`}>
                  {formatStatus(member.memberStatus) || 'Inactive'}
                </span>
              )}
              <div className="absolute inset-0 ring-1 ring-inset ring-white/10" />
            </div>
          </motion.div>

          {/* Member Details Section */}
          <div className="min-w-0 flex-1">
            {/* Primary Info */}
            <div className={verificationLayout ? 'mb-5' : 'mb-6'}>
              <motion.h3
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className={`font-bold text-white ${verificationLayout ? 'mb-3 text-2xl sm:text-3xl' : 'mb-4 text-2xl'}`}
              >
                {formatTitle(member.fullName)}
              </motion.h3>
              
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className={verificationLayout ? 'grid grid-cols-2 gap-2' : 'flex flex-wrap gap-2'}
              >
                {getBadges()}
              </motion.div>
              {verificationLayout && (
                <div className="mt-5 flex items-center" aria-hidden="true">
                  <div className="h-px flex-1 bg-gradient-to-r from-blue-500/70 via-purple-400/40 to-transparent" />
                  <div className="h-1.5 w-1.5 rounded-full bg-blue-400 shadow-[0_0_10px_rgba(96,165,250,0.8)]" />
                </div>
              )}
            </div>

            {/* Detailed Info Grid */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className={`grid gap-3 text-sm ${verificationLayout ? 'grid-cols-2 sm:gap-4' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4'}`}
            >
              <div className={detailClassName}>
                {!verificationLayout && <div className="text-gray-400">Enrollment Number</div>}
                <div className={`${detailValueClassName} break-words text-white ${verificationLayout ? 'font-semibold' : 'font-medium'}`}>{member.permanentEnrollmentNumber || '—'}</div>
              </div>
              {verificationLayout && member.validUntil && (
                <div className={detailClassName}>
                  <div className={`${detailValueClassName} font-medium text-green-400`}>{formatDate(member.validUntil)}</div>
                </div>
              )}
              <div className={detailClassName}>
                {!verificationLayout && <div className="text-gray-400">Languages Known</div>}
                <div className={`${detailValueClassName} text-white font-medium`}>{formatTitle(member.languagesKnown) || '—'}</div>
              </div>
              {!verificationLayout && (
                <div>
                  <div className="text-gray-400">Date of Birth</div>
                  <div className="text-white font-medium">{formatDate(member.dateOfBirth)}</div>
                </div>
              )}
              <div className={detailClassName}>
                {!verificationLayout && <div className="text-gray-400">Current City</div>}
                <div className={`${detailValueClassName} text-white font-medium`}>{formatTitle(member.currentCity) || '—'}</div>
              </div>
              <div className={detailClassName}>
                {!verificationLayout && <div className="text-gray-400">Current District</div>}
                <div className={`${detailValueClassName} text-white font-medium`}>{formatTitle(member.currentDistrict) || '—'}</div>
              </div>
              <div className={detailClassName}>
                {!verificationLayout && <div className="text-gray-400">Current State</div>}
                <div className={`${detailValueClassName} text-white font-medium`}>{formatTitle(member.currentState) || '—'}</div>
              </div>
              <div className={detailClassName}>
                {!verificationLayout && <div className="text-gray-400">Pin Code</div>}
                <div className={`${detailValueClassName} text-white font-medium`}>{member.currentPinCode || '—'}</div>
              </div>
              <div className={detailClassName}>
                {!verificationLayout && <div className="text-gray-400">Blood Group</div>}
                <div className={`${detailValueClassName} text-white font-medium`}>{member.bloodGroup || '—'}</div>
              </div>
              {!verificationLayout && member.validUntil && (
                <div className={detailClassName}>
                  <div className="text-gray-400">Valid Upto</div>
                  <div className={`${detailValueClassName} font-medium text-green-400`}>{formatDate(member.validUntil)}</div>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default SearchCard;
